import { NAV } from '../config.js';

/**
 * 歩行用の当たり判定マップ（上から見たマス目）。
 *
 * スプラットには面が無く、1本のレイでは隙間をすり抜けたり空中のゴミに引っかかったりする。
 * そこで読み込み後に1回だけ空間の表面の点を走査し、高さごとの密度から
 * 「床」「低い家具」「壁」をマス単位で決める。メッシュも表面に点をばらまいて同じ仕組みに載せる。
 *
 * - 床の高さ … 探索上限より下で、十分に密な高さのうち最も低いもの
 * - 家具     … 床から 12〜70cm に密集している（ソファ・テーブル等）
 * - 壁       … 床から 1.0〜2.3m に密集している
 * - 部屋の範囲 … 壁を囲む凸包。撮れていない壁の隙間から外へ出ないようにする
 *
 * 判定できない（床が見つからない等）場合は null を返し、呼び出し側は従来のレイ判定を使う。
 */
export function buildNavGrid(source) {
  if (typeof source?.forEachSurfacePoint !== 'function') return null;

  const origin = source.navOrigin;
  const R = source.navRadius;
  const CELL = NAV.cell;
  const N = Math.round((R * 2) / CELL);
  const ox = origin.x;
  const oz = origin.z;

  // 1回目の走査: 範囲内の表面の点を集める
  const xs = [];
  const ys = [];
  const zs = [];
  // 1 = 水平面の点, 0 = 垂直面の点, 2 = 不明（スプラット）。壁の判定で水平面を除くのに使う
  const flat = [];
  source.forEachSurfacePoint((x, y, z, up) => {
    if (Math.hypot(x - ox, z - oz) > R) return;
    xs.push(x);
    ys.push(y);
    zs.push(z);
    flat.push(up === undefined ? 2 : Math.abs(up) > NAV.horizontalNormal ? 1 : 0);
  });
  if (xs.length < NAV.minSplats) return null;

  const floorY = detectFloor(ys, source.floorSearchTop, source.floorPeakRatio);
  if (floorY === null) return null;

  // 2回目: マスごとに「低い層」と「高い層」の密度を数える
  const low = new Uint32Array(N * N);
  const high = new Uint32Array(N * N);
  for (let i = 0; i < xs.length; i += 1) {
    const gx = Math.floor((xs[i] - ox + R) / CELL);
    const gz = Math.floor((zs[i] - oz + R) / CELL);
    if (gx < 0 || gz < 0 || gx >= N || gz >= N) continue;
    const h = ys[i] - floorY;
    if (h > NAV.lowMin && h < NAV.lowMax) low[gz * N + gx] += 1;
    // ロフトの床や棚板のような水平面は、下を通れるので壁として数えない
    else if (h > NAV.highMin && h < NAV.highMax && flat[i] !== 1) high[gz * N + gx] += 1;
  }

  const wall = new Uint8Array(N * N);
  const furniture = new Uint8Array(N * N);
  const hullPoints = [];
  for (let k = 0; k < N * N; k += 1) {
    if (high[k] >= NAV.wallThreshold) {
      wall[k] = 1;
      hullPoints.push([k % N, (k / N) | 0]);
    }
    if (low[k] >= NAV.furnitureThreshold) furniture[k] = 1;
  }
  if (hullPoints.length < 3) return null;

  const hull = convexHull(hullPoints);
  const inRoom = new Uint8Array(N * N);
  for (let k = 0; k < N * N; k += 1) inRoom[k] = insideHull(hull, k % N, (k / N) | 0) ? 1 : 0;

  const wallGrown = dilate(wall, N, NAV.bodyCells);
  const furnitureGrown = dilate(furniture, N, NAV.bodyCells);

  // キャラが立てる場所: 部屋の中で、壁にも家具にも体がめり込まない
  const blocked = new Uint8Array(N * N);
  for (let k = 0; k < N * N; k += 1) {
    blocked[k] = !inRoom[k] || wallGrown[k] || furnitureGrown[k] ? 1 : 0;
  }
  const center = Math.floor(R / CELL) * N + Math.floor(R / CELL);
  const walkable = floodFill(blocked, N, nearestOpen(blocked, N, center));

  let area = 0;
  for (let k = 0; k < N * N; k += 1) area += walkable[k];
  area *= CELL * CELL;
  if (area < NAV.minAreaM2) return null;

  // カメラが入ってよい場所: 家具の上空は可、壁と部屋の外は不可
  const cameraOpen = new Uint8Array(N * N);
  for (let k = 0; k < N * N; k += 1) cameraOpen[k] = inRoom[k] && !wall[k] ? 1 : 0;

  const indexOf = (x, z) => {
    const gx = Math.floor((x - ox + R) / CELL);
    const gz = Math.floor((z - oz + R) / CELL);
    if (gx < 0 || gz < 0 || gx >= N || gz >= N) return -1;
    return gz * N + gx;
  };

  return Object.freeze({
    floorY,
    area,
    /** 床は1枚なので、歩けるマスなら常に床の高さ */
    heightAt(x, z) {
      const k = indexOf(x, z);
      return k >= 0 && walkable[k] === 1 ? floorY : null;
    },
    isWalkable(x, z) {
      const k = indexOf(x, z);
      return k >= 0 && walkable[k] === 1;
    },
    isCameraOpen(x, z) {
      const k = indexOf(x, z);
      return k >= 0 && cameraOpen[k] === 1;
    },
    /** 指定地点に一番近い歩けるマスの中心を返す（視点ジャンプ時の着地点） */
    nearestWalkable(x, z) {
      const k = indexOf(x, z);
      const found = nearestOpen(inverted(walkable), N, k < 0 ? center : k);
      if (found < 0) return null;
      return {
        x: ((found % N) + 0.5) * CELL - R + ox,
        z: (((found / N) | 0) + 0.5) * CELL - R + oz,
        y: floorY,
      };
    },
  });
}

/**
 * 床の高さを決める。
 * 探索上限より下の高さを刻みごとに数え、最も密な刻みの一定割合以上ある刻みのうち
 * 一番低いものを床とする。天井・ロフトの床も密になるが、それらより下にあるため。
 */
function detectFloor(ys, top, peakRatio) {
  const bin = NAV.floorBin;
  const counts = new Map();
  let below = 0;
  for (const y of ys) {
    if (y >= top) continue;
    below += 1;
    const b = Math.round(y / bin);
    counts.set(b, (counts.get(b) ?? 0) + 1);
  }
  let peak = 0;
  for (const n of counts.values()) peak = Math.max(peak, n);
  // 床らしい面がはっきり無い（物体だけのスキャン等）なら判定しない
  if (peak === 0 || peak < below * peakRatio) return null;

  let lowest = null;
  for (const [b, n] of counts) {
    if (n >= peak * NAV.floorCandidateRatio && (lowest === null || b < lowest)) lowest = b;
  }
  return lowest * bin;
}

function dilate(mask, N, d) {
  if (d <= 0) return mask;
  const out = new Uint8Array(N * N);
  for (let z = 0; z < N; z += 1) {
    for (let x = 0; x < N; x += 1) {
      if (!mask[z * N + x]) continue;
      for (let dz = -d; dz <= d; dz += 1) {
        for (let dx = -d; dx <= d; dx += 1) {
          if (dx * dx + dz * dz > d * d) continue;
          const xx = x + dx;
          const zz = z + dz;
          if (xx >= 0 && zz >= 0 && xx < N && zz < N) out[zz * N + xx] = 1;
        }
      }
    }
  }
  return out;
}

function inverted(mask) {
  const out = new Uint8Array(mask.length);
  for (let k = 0; k < mask.length; k += 1) out[k] = mask[k] ? 0 : 1;
  return out;
}

/** blocked が 0 のマスのうち、start に最も近いものを探す */
function nearestOpen(blocked, N, start) {
  if (start < 0) return -1;
  if (!blocked[start]) return start;
  const sx = start % N;
  const sz = (start / N) | 0;
  for (let r = 1; r < N; r += 1) {
    let best = -1;
    let bestD = Infinity;
    for (let dz = -r; dz <= r; dz += 1) {
      for (let dx = -r; dx <= r; dx += 1) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
        const x = sx + dx;
        const z = sz + dz;
        if (x < 0 || z < 0 || x >= N || z >= N) continue;
        const k = z * N + x;
        const d = dx * dx + dz * dz;
        if (!blocked[k] && d < bestD) {
          best = k;
          bestD = d;
        }
      }
    }
    if (best >= 0) return best;
  }
  return -1;
}

function floodFill(blocked, N, start) {
  const out = new Uint8Array(N * N);
  if (start < 0) return out;
  const stack = [start];
  out[start] = 1;
  while (stack.length > 0) {
    const k = stack.pop();
    const x = k % N;
    const z = (k / N) | 0;
    for (const [dx, dz] of NEIGHBORS) {
      const xx = x + dx;
      const zz = z + dz;
      if (xx < 0 || zz < 0 || xx >= N || zz >= N) continue;
      const kk = zz * N + xx;
      if (out[kk] || blocked[kk]) continue;
      out[kk] = 1;
      stack.push(kk);
    }
  }
  return out;
}

const NEIGHBORS = [[1, 0], [-1, 0], [0, 1], [0, -1]];

function cross(o, a, b) {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

/** Andrew の monotone chain による凸包 */
function convexHull(points) {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const lower = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper = [];
  for (const p of [...sorted].reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

function insideHull(hull, x, z) {
  for (let i = 0; i < hull.length; i += 1) {
    if (cross(hull[i], hull[(i + 1) % hull.length], [x, z]) < 0) return false;
  }
  return true;
}
